import * as Collapsible from '@radix-ui/react-collapsible'
import * as Dialog from '@radix-ui/react-dialog'
import { ChevronDownIcon, Cross2Icon, PlusIcon, TrashIcon } from '@radix-ui/react-icons'
import { useQueryClient } from '@tanstack/react-query'
import React, { useState } from 'react'
import { match } from 'ts-pattern'
import { typography } from '../../main.css.ts'
import { mapToolToLogoAndName } from '../../routes/analysis-details-page'
import { useDeleteWorkflow, useGetScales, useGetWorkflows, usePostCreateWorkflow } from '../../utils/api-client/api-hooks.ts'
import { CreateWorkflowRequest, Repository, Tool, ToolLowercase } from '../../utils/api-client/api-schemas.ts'
import { useTheme } from '../../utils/higher-order-components/with-theme'
import { useAddToast } from '../../utils/higher-order-components/with-toasts'
import { getRepositoryIcon } from '../../utils/repository-utils'
import { DefaultButton } from '../buttons/default-button.tsx'
import { GhostButton } from '../buttons/ghost-button.tsx'
import LightningIcon from '../icons/lightning-icon.svg?react'
import WarningIcon from '../icons/warning-icon-trace.svg?react'
import { Checkbox } from '../input/checkbox'
import { InputContainer } from '../input/input-container'
import { InputGroup } from '../input/input-group'
import { SelectBox } from '../input/select-box.tsx'
import { Slider } from '../input/slider'
import { TextInput } from '../input/text-input'
import { TooltipWithHover } from '../tooltip'
import { getActionButtonText } from './finding-fix-drawer.tsx'
import * as styles from './upload-scan-form-drawer.css'
import * as localStyles from './workflow-drawer.css'

type WorkflowItem = {
  id: string
  name: string | null
}

type WorkflowParams = {
  name?: string
  tool: ToolLowercase
  cadence?: 'daily' | 'weekly'
  selectedSeverities?: string[]
  minimumSeverityScore?: number
  maximumSeverityScore?: number
  type: 'scan' | 'scheduled'
  branch?: string
  limitFixes?: boolean
  fixesLimit?: number
  fixConfidence?: 'high' | 'medium' | 'low'
}

const toolsShowingSlider = ['polaris', 'codeql', 'checkmarx']

type WorkflowDrawerPureProps = {
  repositoryDisplayName: string
  repositoryType: Repository['type']
  drawerContent: 'create' | 'manage'
  onSetDrawerContent?: (content: 'create' | 'manage') => void
  isManageWorkflowsOpen?: boolean
  onManageWorkflowsOpenChange?: (open: boolean) => void
  workflows?: WorkflowItem[]
  onCreateWorkflow?: (params: WorkflowParams) => void
  onDeleteWorkflow?: (workflowId: string) => void
  handleCloseDrawer?: () => void
  onCreateWorkflowAndContinue?: (params: WorkflowParams) => void
}

export function WorkflowDrawerPure({
  repositoryDisplayName,
  repositoryType,
  handleCloseDrawer = () => {},
  drawerContent = 'create',
  onCreateWorkflow = () => {},
  onSetDrawerContent = () => {},
  isManageWorkflowsOpen = false,
  onManageWorkflowsOpenChange = () => {},
  workflows = [],
  onDeleteWorkflow = () => {},
  onCreateWorkflowAndContinue = () => {},
}: WorkflowDrawerPureProps) {
  const [selectedType, setSelectedType] = useState<string>()
  const [selectedTool, setSelectedTool] = useState<string>()
  const [selectedCadence, setSelectedCadence] = useState<string>()
  const [workflowName, setWorkflowName] = useState<string>('')
  const [branch, setBranch] = useState<string>('')
  const [limitFixes, setLimitFixes] = useState<boolean>(true)
  const [fixesLimit, setFixesLimit] = useState<number>(1)
  const { theme } = useTheme()
  const [workflowToDelete, setWorkflowToDelete] = useState<string | null>(null)
  const [selectedSeverities, setSelectedSeverities] = useState<string[]>([])
  const { data: scales } = useGetScales()
  const cvssScale = scales._embedded.items.find(scale => scale.id === 'cvss')
  const cvssMin = cvssScale?.min ?? 0
  const cvssMax = cvssScale?.max ?? 10

  const [selectedRange, setSelectedRange] = useState<[number, number]>([cvssMin, cvssMax])
  const [fixConfidence, setFixConfidence] = useState<'high' | 'medium' | 'low'>()

  // Get the scale based on selected tool
  const getScaleForTool = (tool: string | undefined) => {
    if (!tool) return null

    return match(tool.toLowerCase())
      .with('appscan', () => scales._embedded.items.find(scale => scale.id === 'appscan'))
      .with('veracode', () => scales._embedded.items.find(scale => scale.id === 'veracode'))
      .otherwise(() => scales._embedded.items.find(scale => scale.id === 'sonar-merged'))
  }

  const selectedScale = getScaleForTool(selectedTool)
  const severities = [...(selectedScale?.severities ?? [])].sort((a, b) => b.rank - a.rank)

  const SCMIcon = getRepositoryIcon({ type: repositoryType, className: localStyles.scmIcon })

  const validTools: Tool[] = [
    Tool.Enum.APPSCAN,
    Tool.Enum.POLARIS,
    Tool.Enum.CHECKMARX,
    Tool.Enum.CODEQL,
    Tool.Enum.SEMGREP,
    Tool.Enum.SNYK,
    Tool.Enum.SONAR,
    Tool.Enum.VERACODE,
  ]
  const tools: Tool[] = Tool.options.filter(tool => validTools.includes(tool as Tool))

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!selectedTool) return
    if (selectedType === 'scheduled' && !selectedCadence) return

    const params: WorkflowParams = {
      name: workflowName.length > 0 ? workflowName : undefined,
      tool: selectedTool.toLowerCase() as ToolLowercase,
      type: selectedType as 'scan' | 'scheduled',
      ...(selectedType === 'scheduled' && {
        cadence: selectedCadence as 'daily' | 'weekly',
      }),
      ...(selectedType === 'scan' && {
        branch,
        limitFixes,
        ...(limitFixes && { fixesLimit }),
      }),
      selectedSeverities,
      ...(fixConfidence && { fixConfidence }),
    }

    if (toolsShowingSlider.includes(selectedTool.toLowerCase())) {
      if (selectedRange[0] !== cvssMin) {
        params.minimumSeverityScore = selectedRange[0]
      }
      if (selectedRange[1] !== cvssMax) {
        params.maximumSeverityScore = selectedRange[1]
      }
    }

    onCreateWorkflow(params)
  }

  const resetForm = () => {
    setSelectedType(undefined)
    setSelectedTool(undefined)
    setSelectedCadence(undefined)
    setWorkflowName('')
    setBranch('')
    setFixConfidence(undefined)
  }

  const capitalizeFirstLetter = (str: string) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()

  const renderSeveritySection = () => {
    if (!selectedTool) return null

    const toolsUsingSarif = ['semgrep', 'snyk']

    if (toolsShowingSlider.includes(selectedTool.toLowerCase())) {
      return (
        <div className={localStyles.severityContainer}>
          <InputContainer label="Severity range">
            <Slider
              defaultValue={[cvssMin, cvssMax]}
              min={cvssMin}
              max={cvssMax}
              value={selectedRange}
              onValueChange={setSelectedRange}
              aria-label="Severity threshold range"
            />
          </InputContainer>
        </div>
      )
    }

    if (toolsUsingSarif.includes(selectedTool.toLowerCase())) {
      const sarifScale = scales._embedded.items.find(scale => scale.id === 'sarif-levels')
      const severities = [...(sarifScale?.severities ?? [])].sort((a, b) => b.rank - a.rank)
      return (
        <div className={localStyles.severityContainer}>
          <p className={`${typography.paragraphBold} ${localStyles.severityHeading}`}>Severity</p>
          <div className={localStyles.checkboxGroup}>
            {severities.map(severity => (
              <div key={severity.label} className={localStyles.severityItem}>
                <Checkbox
                  id={severity.label.toLowerCase()}
                  checked={selectedSeverities.includes(severity.label.toLowerCase())}
                  onCheckedChange={checked => {
                    setSelectedSeverities(prev =>
                      checked ? [...prev, severity.label.toLowerCase()] : prev.filter(s => s !== severity.label.toLowerCase())
                    )
                  }}
                  state="default"
                />
                <label htmlFor={severity.label.toLowerCase()} className={`${typography.paragraph} ${localStyles.severityLabel}`}>
                  {capitalizeFirstLetter(severity.label)}
                </label>
              </div>
            ))}
          </div>
        </div>
      )
    }

    // Default to sonar-merged scale for other tools
    return (
      <div className={localStyles.severityContainer}>
        <p className={`${typography.paragraphBold} ${localStyles.severityHeading}`}>Severity</p>
        <div className={localStyles.checkboxGroup}>
          {severities.map(severity => (
            <div key={severity.label} className={localStyles.severityItem}>
              <Checkbox
                id={severity.label.toLowerCase()}
                checked={selectedSeverities.includes(severity.label.toLowerCase())}
                onCheckedChange={checked => {
                  setSelectedSeverities(prev =>
                    checked ? [...prev, severity.label.toLowerCase()] : prev.filter(s => s !== severity.label.toLowerCase())
                  )
                }}
                state="default"
              />
              <label htmlFor={severity.label.toLowerCase()} className={`${typography.paragraph} ${localStyles.severityLabel}`}>
                {capitalizeFirstLetter(severity.label)}
              </label>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <Dialog.Root open={true} modal={false}>
      <Dialog.Portal>
        <Dialog.Content className={styles.content}>
          {drawerContent === 'create' ? (
            <>
              <Dialog.Title className={`${styles.title} ${localStyles.title}`}>
                <div className={localStyles.repoName}>
                  {SCMIcon}
                  {repositoryDisplayName}
                </div>
                New workflow
              </Dialog.Title>
              <Dialog.Description className={`${styles.description} ${localStyles.separator}`}>
                Configure events and scope to result in designated actions
              </Dialog.Description>

              <form className={localStyles.workflowFormContainer} onSubmit={handleSubmit}>
                <div className={styles.flexContainer}>
                  <SelectBox
                    label="Type"
                    name="workflowType"
                    placeholder="Select workflow"
                    value={selectedType}
                    onValueChange={value => setSelectedType(value as string)}
                    options={[
                      { value: 'scheduled', label: 'Scheduled fix' },
                      { value: 'scan', label: 'New scan' },
                    ]}
                  />
                </div>

                {selectedType === 'scheduled' && (
                  <>
                    <div className={localStyles.findingsContainer}>
                      <div className={localStyles.sectionHeading}>Which findings would you like to address?</div>
                      <SelectBox
                        label="Tool"
                        name="tool"
                        value={selectedTool}
                        onValueChange={value => setSelectedTool(value)}
                        options={tools
                          .filter(tool => tool != 'PIXEE')
                          .map(tool => {
                            const { name, logoHref } = mapToolToLogoAndName(tool, theme)
                            return {
                              value: tool,
                              label: (
                                <span className={styles.selectItemText}>
                                  <img src={logoHref} alt={`${name} logo`} width="16" height="16" style={{ marginRight: '8px' }} />
                                  {name}
                                </span>
                              ),
                            }
                          })}
                        placeholder="Select tool"
                      />
                      {selectedTool && renderSeveritySection()}
                    </div>

                    <InputGroup title="Customize the fixes you receive">
                      <SelectBox
                        label="Minimum fix confidence"
                        placeholder="Select confidence level"
                        value={fixConfidence}
                        onValueChange={value => setFixConfidence(value as 'high' | 'medium' | 'low')}
                        options={[
                          { value: 'low', label: 'Low' },
                          { value: 'medium', label: 'Medium' },
                          { value: 'high', label: 'High' },
                        ]}
                      />
                    </InputGroup>

                    <div className={localStyles.findingsContainer}>
                      <div className={localStyles.sectionHeading}>Define a schedule</div>
                      <SelectBox
                        label="Cadence"
                        name="cadence"
                        value={selectedCadence}
                        onValueChange={value => setSelectedCadence(value)}
                        options={[
                          { value: 'daily', label: 'Daily' },
                          { value: 'weekly', label: 'Weekly' },
                        ]}
                        placeholder="Select cadence"
                      />
                    </div>

                    <div className={styles.flexContainer}>
                      <TextInput
                        id="workflowName"
                        name="workflowName"
                        label="Name"
                        value={workflowName}
                        onChange={value => setWorkflowName(value.toString())}
                        placeholder="Override the default workflow name with your own"
                        isOptional
                      />
                    </div>
                  </>
                )}

                {selectedType === 'scan' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '48px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                      <div className={localStyles.sectionHeading}>What would you like to monitor?</div>
                      <SelectBox
                        label="Tool"
                        name="tool"
                        value={selectedTool}
                        onValueChange={value => setSelectedTool(value)}
                        options={tools
                          .filter(tool => tool != 'PIXEE')
                          .map(tool => {
                            const { name, logoHref } = mapToolToLogoAndName(tool, theme)
                            return {
                              value: tool,
                              label: (
                                <span className={styles.selectItemText}>
                                  <img src={logoHref} alt={`${name} logo`} width="16" height="16" style={{ marginRight: '8px' }} />
                                  {name}
                                </span>
                              ),
                            }
                          })}
                        placeholder="Select tool"
                      />
                      {selectedTool && renderSeveritySection()}
                    </div>

                    <div className={styles.flexContainer}>
                      <TextInput
                        id="branch"
                        name="branch"
                        label="Branch/Prefix"
                        value={branch}
                        onChange={value => setBranch(value.toString())}
                        placeholder="Enter branch name or prefix"
                        isOptional
                        helperText="The default branch will be used if nothing is specified"
                      />
                    </div>

                    <InputGroup title="Customize the fixes you receive">
                      <SelectBox
                        label="Minimum fix confidence"
                        placeholder="Select confidence level"
                        value={fixConfidence}
                        onValueChange={value => setFixConfidence(value as 'high' | 'medium' | 'low')}
                        options={[
                          { value: 'low', label: 'Low' },
                          { value: 'medium', label: 'Medium' },
                          { value: 'high', label: 'High' },
                        ]}
                      />
                    </InputGroup>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                      <div className={localStyles.sectionHeading}>Define the action to take</div>
                      <SelectBox
                        label="Action"
                        value="create-patch"
                        disabled={true}
                        options={[{ value: 'create-patch', label: getActionButtonText(repositoryType) }]}
                      />
                      <div className={localStyles.fixesLimitContainer}>
                        <div className={localStyles.fixesLimitText}>
                          <Checkbox
                            id="limit-fixes"
                            checked={limitFixes}
                            onCheckedChange={checked => setLimitFixes(checked === true)}
                            state="default"
                          />
                          <label
                            htmlFor="limit-fixes"
                            className={`${typography.paragraph}`}
                            style={{ color: 'var(--colors-neutral-neutral10)', cursor: 'pointer' }}
                          >
                            Limit the number of fixes per{' '}
                            {getActionButtonText(repositoryType).toLowerCase().replace('create ', '').replace(' to branch', '')}
                          </label>
                        </div>
                        <TextInput
                          type="number"
                          value={fixesLimit.toString()}
                          onChange={value => setFixesLimit(Math.max(1, parseInt(value.toString()) || 1))}
                          width={80}
                        />
                      </div>
                    </div>

                    <div className={styles.flexContainer}>
                      <TextInput
                        id="workflowName"
                        name="workflowName"
                        label="Name"
                        value={workflowName}
                        onChange={value => setWorkflowName(value.toString())}
                        placeholder="Override the default workflow name with your own"
                        isOptional
                      />
                    </div>
                  </div>
                )}

                <div className={localStyles.buttonContainer}>
                  <a
                    href="#"
                    className={localStyles.manageWorkflowsLink}
                    onClick={e => {
                      e.preventDefault()
                      if (selectedType === 'scan' ? selectedTool : selectedTool && selectedCadence) {
                        onManageWorkflowsOpenChange(true)
                      } else {
                        onSetDrawerContent('manage')
                      }
                    }}
                  >
                    <LightningIcon className={localStyles.workflowIcon} />
                    <span>Manage workflows</span>
                  </a>
                  <div className={localStyles.actionButtons}>
                    <GhostButton type="secondary" onClick={handleCloseDrawer}>
                      Cancel
                    </GhostButton>
                    <DefaultButton
                      buttonType="primary"
                      state={
                        selectedType === 'scan' ? (selectedTool ? 'default' : 'disabled') : selectedTool && selectedCadence ? 'default' : 'disabled'
                      }
                      buttonRole="submit"
                    >
                      Create workflow
                    </DefaultButton>
                  </div>
                </div>
              </form>
            </>
          ) : (
            <>
              <Dialog.Close asChild>
                <button className={localStyles.closeButton} onClick={handleCloseDrawer} aria-label="Close dialog">
                  <Cross2Icon width={24} height={24} />
                </button>
              </Dialog.Close>
              <div className={localStyles.manageWorkflowsContent}>
                <div className={localStyles.manageWorkflowsHeader}>
                  <RepoInfo repositoryType={repositoryType} repositoryDisplayName={repositoryDisplayName} />
                  <div className={`${typography.h5} ${localStyles.manageWorkflowsTitle}`}>Manage workflows</div>
                  <div className={localStyles.manageWorkflowsActions}>
                    <GhostButton type="default" onClick={() => onSetDrawerContent('create')}>
                      <PlusIcon className={localStyles.createNewIcon} />
                      Create new
                    </GhostButton>
                  </div>
                </div>
                {workflows.length > 0 ? (
                  <Collapsible.Root defaultOpen>
                    <div className={localStyles.collapsibleHeader}>
                      <Collapsible.Trigger asChild>
                        <button className={localStyles.collapsibleTrigger}>
                          <div className={localStyles.scheduledFixesHeading}>Scheduled fixes</div>
                          <ChevronDownIcon className={localStyles.collapsibleChevron} />
                        </button>
                      </Collapsible.Trigger>
                    </div>
                    <Collapsible.Content>
                      <ul className={localStyles.collapsibleContent}>
                        {workflows.map(workflow => (
                          <WorkflowItem
                            key={workflow.id}
                            id={workflow.id}
                            title={workflow.name || 'Unnamed workflow'}
                            onDelete={() => setWorkflowToDelete(workflow.id)}
                          />
                        ))}
                      </ul>
                    </Collapsible.Content>
                  </Collapsible.Root>
                ) : (
                  <div className={localStyles.dialogSubtitle}>
                    <div className={typography.paragraphItalic}>No workflows found</div>
                  </div>
                )}
              </div>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>

      <Dialog.Root open={isManageWorkflowsOpen} onOpenChange={onManageWorkflowsOpenChange}>
        <Dialog.Portal>
          <Dialog.Overlay className={localStyles.dialogOverlay} />
          <Dialog.Content className={localStyles.dialogContent}>
            {drawerContent === 'create' ? (
              <>
                <Dialog.Title className={localStyles.dialogTitle}>
                  <WarningIcon className={localStyles.warningIcon} />
                  Create workflow?
                </Dialog.Title>
                <div className={localStyles.dialogMiddle}>
                  <div className={localStyles.dialogMessage}>You have a workflow in progress. How would you like to proceed?</div>
                  <div className={localStyles.dialogSubtitle}>
                    <RepoInfo repositoryType={repositoryType} repositoryDisplayName={repositoryDisplayName} />
                    <div className={localStyles.workflowName}>
                      {workflowName || getDefaultWorkflowName(selectedTool, selectedCadence) || 'Unnamed'}
                    </div>
                  </div>
                </div>
                <div className={localStyles.dialogButtons}>
                  <GhostButton type="secondary" onClick={() => onManageWorkflowsOpenChange(false)}>
                    Cancel
                  </GhostButton>
                  <DefaultButton
                    buttonType="secondary"
                    onClick={() => {
                      resetForm()
                      onManageWorkflowsOpenChange(false)
                      onSetDrawerContent('manage')
                    }}
                  >
                    Abandon and continue
                  </DefaultButton>
                  <DefaultButton
                    buttonType="primary"
                    onClick={() => {
                      if (!selectedTool) return
                      if (selectedType === 'scheduled' && !selectedCadence) return

                      const params: WorkflowParams = {
                        name: workflowName.length > 0 ? workflowName : undefined,
                        tool: selectedTool.toLowerCase() as ToolLowercase,
                        type: selectedType as 'scan' | 'scheduled',
                        ...(selectedType === 'scheduled' && {
                          cadence: selectedCadence as 'daily' | 'weekly',
                        }),
                        ...(selectedType === 'scan' && {
                          branch,
                          limitFixes,
                          ...(limitFixes && { fixesLimit }),
                        }),
                        selectedSeverities,
                        ...(fixConfidence && { fixConfidence }),
                      }

                      if (toolsShowingSlider.includes(selectedTool.toLowerCase())) {
                        if (selectedRange[0] !== cvssMin) {
                          params.minimumSeverityScore = selectedRange[0]
                        }
                        if (selectedRange[1] !== cvssMax) {
                          params.maximumSeverityScore = selectedRange[1]
                        }
                      }

                      onCreateWorkflowAndContinue(params)
                      resetForm()
                    }}
                  >
                    Create and continue
                  </DefaultButton>
                </div>
              </>
            ) : (
              <div>hi</div>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <Dialog.Root open={!!workflowToDelete} onOpenChange={() => setWorkflowToDelete(null)}>
        <Dialog.Portal>
          <Dialog.Overlay className={localStyles.dialogOverlay} />
          <Dialog.Content className={localStyles.dialogContent}>
            <Dialog.Title className={localStyles.dialogTitle}>
              <WarningIcon className={localStyles.warningIcon} />
              Delete workflow?
            </Dialog.Title>
            <div className={localStyles.dialogMiddle}>
              <div className={localStyles.dialogMessage}>You are about to delete the following workflow:</div>
              <div className={localStyles.dialogSubtitle}>
                <RepoInfo repositoryType={repositoryType} repositoryDisplayName={repositoryDisplayName} />
                <div className={localStyles.workflowName}>{workflows.find(w => w.id === workflowToDelete)?.name || 'Unnamed workflow'}</div>
              </div>
              <div className={localStyles.dialogMessage}>This action cannot be undone. Do you wish to proceed?</div>
            </div>
            <div className={localStyles.dialogButtons}>
              <GhostButton type="secondary" onClick={() => setWorkflowToDelete(null)}>
                Cancel
              </GhostButton>
              <DefaultButton
                buttonType="destructive"
                onClick={() => {
                  if (workflowToDelete) {
                    onDeleteWorkflow(workflowToDelete)
                    setWorkflowToDelete(null)
                  }
                }}
              >
                Delete workflow
              </DefaultButton>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </Dialog.Root>
  )
}

const RepoInfo = ({ repositoryType, repositoryDisplayName }: { repositoryType: Repository['type']; repositoryDisplayName: string }) => (
  <div className={localStyles.dialogRepoInfo}>
    {getRepositoryIcon({ type: repositoryType, className: localStyles.dialogRepoIcon })}
    <span>{repositoryDisplayName}</span>
  </div>
)

type WorkflowItemProps = {
  title: string
  id: string
  onDelete: (id: string) => void
}

const WorkflowItem = ({ title, id, onDelete }: WorkflowItemProps) => {
  return (
    <li className={localStyles.workflowItem}>
      <div className={localStyles.workflowItemRow}>
        <div>
          <div className={localStyles.workflowItemTitle}>{title}</div>
        </div>
        <TooltipWithHover
          size="small"
          trigger={
            <button className={localStyles.deleteButton} onClick={() => onDelete(id)} aria-label="Delete workflow">
              <TrashIcon className={localStyles.trashIcon} />
            </button>
          }
        >
          <span>Delete workflow</span>
        </TooltipWithHover>
      </div>
    </li>
  )
}

const getDefaultWorkflowName = (tool: string | undefined, cadence: string | undefined) => {
  if (!tool || !cadence) return ''
  const toolName = tool.charAt(0).toUpperCase() + tool.slice(1).toLowerCase()
  return `${cadence === 'daily' ? 'Daily' : 'Weekly'} ${toolName} fixes`
}

type WorkflowDrawerProps = {
  repositoryId: string
  repositoryDisplayName: string
  repositoryType: Repository['type']
  handleCloseDrawer: () => void
  drawerContent: 'create' | 'manage'
  onSetDrawerContent: (content: 'create' | 'manage') => void
}

export const WorkflowDrawer: React.FC<WorkflowDrawerProps> = ({
  repositoryId,
  repositoryDisplayName,
  repositoryType,
  handleCloseDrawer,
  drawerContent,
  onSetDrawerContent,
}) => {
  const { handleAddToastWithTimeout } = useAddToast()
  const [isManageWorkflowsOpen, setIsManageWorkflowsOpen] = useState(false)

  const queryClient = useQueryClient()

  const createWorkflowMutation = usePostCreateWorkflow({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows', repositoryId] })
      handleAddToastWithTimeout({ message: <>Workflow created successfully</>, variant: 'success' })
      handleCloseDrawer()
    },
    onError: error => {
      handleAddToastWithTimeout({
        message: <>Failed to create workflow: {error.message}</>,
        variant: 'error',
      })
    },
  })

  const createWorkflowAndContinueMutation = usePostCreateWorkflow({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows', repositoryId] })
      handleAddToastWithTimeout({ message: <>Workflow created successfully</>, variant: 'success' })
      setIsManageWorkflowsOpen(false)
      onSetDrawerContent('manage')
    },
    onError: error => {
      handleAddToastWithTimeout({
        message: <>Failed to create workflow: {error.message}</>,
        variant: 'error',
      })
    },
  })

  const deleteWorkflowMutation = useDeleteWorkflow({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows', repositoryId] })
      handleAddToastWithTimeout({ message: <>Workflow deleted successfully</>, variant: 'success' })
    },
    onError: error => {
      handleAddToastWithTimeout({
        message: <>Failed to delete workflow: {error.message}</>,
        variant: 'error',
      })
    },
  })

  const { data: workflowsData } = useGetWorkflows({ repositoryId })

  const handleCreateWorkflow = (params: WorkflowParams) => {
    const event =
      params.type === 'scheduled'
        ? {
            type: 'schedule' as const,
            cadence: params.cadence!,
            start: undefined,
          }
        : {
            type: 'new-scan' as const,
            ...(params.branch ? { branch: params.branch } : {}),
          }

    const workflowRequest: CreateWorkflowRequest = {
      tool: params.tool,
      ...(params.name && { name: params.name }),
      event,
      action: {
        type: 'create-patch',
        findings: {
          ...(params.selectedSeverities?.length && {
            include_severity_labels: params.selectedSeverities,
          }),
          ...(params.minimumSeverityScore !== undefined && {
            minimum_severity_score: params.minimumSeverityScore,
          }),
          ...(params.maximumSeverityScore !== undefined && {
            maximum_severity_score: params.maximumSeverityScore,
          }),
        },
        ...(params.fixConfidence && {
          fixes: {
            minimum_fix_confidence: params.fixConfidence,
          },
        }),
        ...(params.limitFixes &&
          params.fixesLimit && {
            limit: {
              type: 'finding-count',
              max: params.fixesLimit,
            },
          }),
      },
    }

    createWorkflowMutation.mutate({ repositoryId, workflow: workflowRequest as any })
  }

  const handleCreateWorkflowAndContinue = (params: WorkflowParams) => {
    const event =
      params.type === 'scheduled'
        ? {
            type: 'schedule' as const,
            cadence: params.cadence!,
            start: undefined,
          }
        : {
            type: 'new-scan' as const,
            ...(params.branch ? { branch: params.branch } : {}),
          }

    const workflowRequest: CreateWorkflowRequest = {
      tool: params.tool,
      ...(params.name && { name: params.name }),
      event,
      action: {
        type: 'create-patch',
        findings: {
          ...(params.selectedSeverities?.length && {
            include_severity_labels: params.selectedSeverities,
          }),
          ...(params.minimumSeverityScore !== undefined && {
            minimum_severity_score: params.minimumSeverityScore,
          }),
          ...(params.maximumSeverityScore !== undefined && {
            maximum_severity_score: params.maximumSeverityScore,
          }),
        },
        ...(params.fixConfidence && {
          fixes: {
            minimum_fix_confidence: params.fixConfidence,
          },
        }),
        ...(params.limitFixes &&
          params.fixesLimit && {
            limit: {
              type: 'finding-count',
              max: params.fixesLimit,
            },
          }),
      },
    }

    createWorkflowAndContinueMutation.mutate({ repositoryId, workflow: workflowRequest as any })
  }

  const handleDeleteWorkflow = (workflowId: string) => {
    deleteWorkflowMutation.mutate({ workflowId })
  }

  return (
    <WorkflowDrawerPure
      repositoryDisplayName={repositoryDisplayName}
      repositoryType={repositoryType}
      handleCloseDrawer={handleCloseDrawer}
      drawerContent={drawerContent}
      onCreateWorkflow={handleCreateWorkflow}
      onSetDrawerContent={onSetDrawerContent}
      isManageWorkflowsOpen={isManageWorkflowsOpen}
      onManageWorkflowsOpenChange={setIsManageWorkflowsOpen}
      onDeleteWorkflow={handleDeleteWorkflow}
      onCreateWorkflowAndContinue={handleCreateWorkflowAndContinue}
      workflows={workflowsData._embedded.items}
    />
  )
}
